import { useEffect, useMemo, useState } from 'react';
import { ScheduleCadence, ScheduleTargetType } from '@/constants';
import FormModal from '@/shared/components/form-modal';
import { MaterialFormContainer, MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { useContents } from '@/services/contents';
import { useCourses } from '@/services/courses';
import { useCreateSchedule } from '@/services/schedules';
import { extractErrorMessage } from '@/utils/helpers';
import { errorBannerStyle } from '@/shared/dark-inline-styles';

interface Props {
    isOpen: boolean;
    toggle: () => void;
}

/**
 * Create-only modal. Edit of cadence/time/enabled happens on the detail page
 * where the queue lives — and the target cannot be changed after creation
 * (changing it would orphan the queue items' payload shape).
 */
const ScheduleModal = ({ isOpen, toggle }: Props) => {
    const { mutateAsync: createSchedule, isPending: isCreating } = useCreateSchedule();
    const { data: contents } = useContents();
    const { data: courses } = useCourses();

    const [name, setName] = useState('');
    const [targetType, setTargetType] = useState<ScheduleTargetType>(ScheduleTargetType.Content);
    const [targetId, setTargetId] = useState('');
    const [publishAtHourUtc, setPublishAtHourUtc] = useState(9);
    const [publishAtMinuteUtc, setPublishAtMinuteUtc] = useState(0);
    const [enabled, setEnabled] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const targetOptions = useMemo(() => {
        if (targetType === ScheduleTargetType.Content) {
            return (contents?.data ?? []).map((c) => ({ id: c.id, label: c.title }));
        }
        return (courses?.data ?? []).map((c) => ({ id: c.id, label: c.title }));
    }, [targetType, contents?.data, courses?.data]);

    useEffect(() => {
        if (!isOpen) {
            setName('');
            setTargetType(ScheduleTargetType.Content);
            setTargetId('');
            setPublishAtHourUtc(9);
            setPublishAtMinuteUtc(0);
            setEnabled(true);
            setError(null);
        }
    }, [isOpen]);

    // Reset targetId when targetType changes — the previous selection is meaningless under the new type.
    useEffect(() => {
        setTargetId('');
    }, [targetType]);

    const validate = (): string | null => {
        if (!name.trim()) return 'Name is required';
        if (!targetId) return 'Pick a target';
        if (publishAtHourUtc < 0 || publishAtHourUtc > 23) return 'Hour must be 0-23';
        if (publishAtMinuteUtc < 0 || publishAtMinuteUtc > 59) return 'Minute must be 0-59';
        return null;
    };

    const handleSave = async () => {
        const v = validate();
        if (v) {
            setError(v);
            return;
        }
        try {
            // callApi never throws on API errors — it resolves { status:'error' }.
            const result = await createSchedule({
                name: name.trim(),
                target: { type: targetType, id: targetId },
                cadence: ScheduleCadence.Daily,
                publishAtHourUtc,
                publishAtMinuteUtc,
                enabled,
            });
            if (result.status === 'error') {
                setError(result.error?.message ?? 'Failed to create schedule.');
                return;
            }
            toggle();
        } catch (e) {
            console.error('Failed to create schedule:', e);
            setError(extractErrorMessage(e));
        }
    };

    return (
        <FormModal
            isOpen={isOpen}
            showDelete={false}
            title="Add Schedule"
            body={
                <MaterialFormContainer onSubmit={handleSave}>
                    {error && (
                        <div style={errorBannerStyle}>{error}</div>
                    )}
                    <MaterialTextField label="Name" value={name} onChange={(e) => setName(e.target.value)} />
                    <MaterialTextField
                        name="select"
                        select
                        label="Target type"
                        value={targetType}
                        onChange={(e) => setTargetType(e.target.value as ScheduleTargetType)}
                    >
                        <MaterialFormOption value={ScheduleTargetType.Content}>Content</MaterialFormOption>
                        <MaterialFormOption value={ScheduleTargetType.Course}>Course</MaterialFormOption>
                    </MaterialTextField>
                    <MaterialTextField
                        name="select"
                        select
                        label={`Target ${targetType}`}
                        value={targetId}
                        onChange={(e) => setTargetId(e.target.value)}
                    >
                        <MaterialFormOption value="">— Pick one —</MaterialFormOption>
                        {targetOptions.map((o) => (
                            <MaterialFormOption key={o.id} value={o.id}>
                                {o.label}
                            </MaterialFormOption>
                        ))}
                    </MaterialTextField>
                    <div style={{ display: 'flex', gap: 12 }}>
                        <MaterialTextField
                            label="Hour (UTC, 0-23)"
                            type="number"
                            value={publishAtHourUtc}
                            onChange={(e) => setPublishAtHourUtc(Number(e.target.value))}
                        />
                        <MaterialTextField
                            label="Minute (UTC, 0-59)"
                            type="number"
                            value={publishAtMinuteUtc}
                            onChange={(e) => setPublishAtMinuteUtc(Number(e.target.value))}
                        />
                    </div>
                    <MaterialTextField
                        name="select"
                        select
                        label="Enabled"
                        value={enabled ? 'yes' : 'no'}
                        onChange={(e) => setEnabled(e.target.value === 'yes')}
                    >
                        <MaterialFormOption value="yes">Enabled</MaterialFormOption>
                        <MaterialFormOption value="no">Disabled</MaterialFormOption>
                    </MaterialTextField>
                </MaterialFormContainer>
            }
            toggle={toggle}
            handleSave={handleSave}
            handleDelete={() => undefined}
            loading={isCreating}
        />
    );
};

export default ScheduleModal;
